import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, FormRenderProps } from 'react-final-form';
import { Stack, Button } from '@mui/material';

import { Page, useAppLayoutContextConsumer, snackbar } from '@occc-app/design-system';
import { LinkItem } from '@occc/ui-design-system';
import { useCreateKioskAssetMutation } from '@occc/graphql';

import { AssetCreateForm } from './components/asset-create-form';
import { validate } from './asset-create.validation';
import { AssetCreateFormValues } from './assets-create.types';

import { routeCreator } from '../../touchpoint.router';
import {
  ASSET_CREATE_SUCCESS_MESSAGE,
  ASSET_CREATE_ERROR_MESSAGE,
} from '../../touchpoint.constants';

const PAGE_TITLE = 'Add an asset';

const initialValues: Partial<AssetCreateFormValues> = {
  name: '',
  description: '',
  landscapeImage: null,
  squareImage: null,
  portraitImage: null,
  landscapeImageUrl: null,
  squareImageUrl: null,
  portraitImageUrl: null,
};

export const AssetsCreatePage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createKioskAsset] = useCreateKioskAssetMutation();

  const breadcrumbLinks: LinkItem[] = useMemo(
    () => [
      {
        to: routeCreator.assetsList(),
        label: 'Touchpoint',
      },
      {
        key: routeCreator.assetsCreate(),
        label: 'Assets',
      },
    ],
    [],
  );

  useAppLayoutContextConsumer(breadcrumbLinks);

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onSubmit = useCallback(
    async (values: AssetCreateFormValues) => {
      setIsSubmitting(true);

      try {
        // All three image URLs are required for create
        if (!values.squareImageUrl || !values.landscapeImageUrl || !values.portraitImageUrl) {
          snackbar.error(
            'All three images (landscape, square, and portrait) are required. Please upload all images.',
          );
          setIsSubmitting(false);
          return;
        }

        // Create the asset with uploaded URLs
        const { data } = await createKioskAsset({
          variables: {
            input: {
              name: values.name.trim(),
              description: values.description.trim() || undefined,
              imageSet: {
                landscape: values.landscapeImageUrl,
                square: values.squareImageUrl,
                portrait: values.portraitImageUrl,
              },
            },
          },
          refetchQueries: ['kioskAssets'],
        });

        if (data?.createKioskAsset) {
          snackbar.success(ASSET_CREATE_SUCCESS_MESSAGE);
          navigate(routeCreator.assetsList());
        } else {
          snackbar.error(ASSET_CREATE_ERROR_MESSAGE);
          setIsSubmitting(false);
        }
      } catch (error) {
        console.error('Error creating asset:', error);
        snackbar.error(ASSET_CREATE_ERROR_MESSAGE);
        setIsSubmitting(false);
      }
    },
    [createKioskAsset, navigate],
  );

  const renderForm = useCallback(
    ({ handleSubmit, valid, submitting }: FormRenderProps<AssetCreateFormValues>) => {
      const isDisabled = submitting || isSubmitting || !valid;

      return (
        <Page
          browserTabTitle={PAGE_TITLE}
          pageHeaderProps={{
            title: PAGE_TITLE,
            subtitle:
              'Add a new image asset for your in-store ads. Upload a visual and your relevant details.',
          }}
          actions={
            <Stack direction='row' spacing={1}>
              <Button variant='outlined' color='secondary' onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant='contained'
                color='secondary'
                onClick={handleSubmit}
                disabled={isDisabled}
              >
                Save
              </Button>
            </Stack>
          }
        >
          <AssetCreateForm handleSubmit={handleSubmit} />
        </Page>
      );
    },
    [handleCancel, isSubmitting],
  );

  return (
    <Form<AssetCreateFormValues>
      onSubmit={onSubmit}
      render={renderForm}
      validate={validate}
      initialValues={initialValues}
    />
  );
};
